import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Languages, Film, ExternalLink } from 'lucide-react';
import { RevenueOverTimeChart, AcquisitionRetentionChart, StatWidget } from '@/components/organisms';
import { Delta, PlatformIcon } from '@/components/primitives';
import { TitleProfileSkeleton } from './TitleProfileSkeleton';
import { apiUrl } from '@/lib/api';
import { formatUsd, formatUsdCompact, formatCount, formatShare, directionOf, prettyQuarter, shortPlatform } from '@/lib/format';
import { fetchTitleRevenue, fetchTitleTimeSeries, fetchRankWindow, type TitleRevenueRow } from '@/lib/titleCharts';
import { getDemoData } from '@/data/demoData';
import styles from './TitleProfile.module.css';

const POSTER_CDN = 'https://cdn-fastly.parrotanalytics.com/images/poster-hyphen';
const BG_CDN = 'https://cdn-fastly.parrotanalytics.com/images/background-hyphen';

interface TitleDetails {
  parrot_id: string;
  short_id: string;
  display_name: string;
  content_type: string;
  /** "Series", "Film", … */
  content_sub_type?: string;
  release_year: number;
  main_genre: string;
  sub_genre: string;
  /** ISO country code(s) of origin, comma-separated: "JP" or "US,GB". */
  countries?: string;
  /** ISO language code: "ja". */
  original_language?: string;
  total_seasons?: number;
  total_episodes?: number;
  short_description?: string;
  imdb_id?: string;
}

/** "game-show" → "Game Show" */
const titleCase = (v: string) => v.replace(/[-_]+/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

/** "JP" → 🇯🇵 (regional-indicator pair); '' for anything that isn't two letters. */
const flagOf = (code: string) =>
  /^[A-Za-z]{2}$/.test(code)
    ? String.fromCodePoint(...[...code.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)))
    : '';

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
const nameOf = (fn: Intl.DisplayNames, code: string) => {
  try {
    return fn.of(code) ?? code;
  } catch {
    return code;
  }
};

/* The title metadata endpoint returns its payload bare — no { data } envelope. */
async function fetchTitleDetails(id: string): Promise<TitleDetails> {
  const res = await fetch(apiUrl(`/titles/${id}`));
  if (!res.ok) throw new Error('Failed to fetch title');
  return res.json();
}

/** Latest quarter_end_date present in the revenue rows. */
function latestQuarterOf(rows: TitleRevenueRow[]): string {
  return rows.reduce((max, r) => (r.quarter_end_date > max ? r.quarter_end_date : max), '');
}

export function TitleProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data: titleData, isLoading: titleLoading } = useQuery({
    queryKey: ['title', id],
    queryFn: () => fetchTitleDetails(id!),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });

  const {
    data: revenueData,
    isLoading: revenueLoading,
    isError: revenueError,
  } = useQuery({
    queryKey: ['title-revenue', id],
    queryFn: () => fetchTitleRevenue(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  // Pre-aggregated quarterly series — authoritative source for the QoQ KPI.
  const { data: timeSeriesData } = useQuery({
    queryKey: ['title-time-series', id],
    queryFn: () => fetchTitleTimeSeries(id!),
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
  });

  /* All-time revenue rank across every platform and region — the banner badge. */
  const { data: rankData } = useQuery({
    queryKey: ['title-rank-alltime', id],
    queryFn: () => fetchRankWindow(id!, { timePeriod: 'all', window: 1 }),
    enabled: !!id,
    staleTime: 10 * 60 * 1000,
  });

  const title = titleData;
  const revenue = revenueData ?? [];
  const timeSeries = timeSeriesData ?? [];

  // Demo overlay — only the 8 hero titles; never presented as pipeline coverage.
  const demoData = id ? getDemoData(id) : undefined;

  const latestQuarter = latestQuarterOf(revenue);
  const latestData = revenue.filter((r) => r.quarter_end_date === latestQuarter);

  const totalRevenue = latestData.reduce((sum, r) => sum + (r.revenue_contribution_usd || 0), 0);
  const totalRetained = latestData.reduce((sum, r) => sum + (r.retained_usd || 0), 0);
  const totalSubsRetained = latestData.reduce((sum, r) => sum + (r.subscribers_retained || 0), 0);
  const avgShare =
    latestData.length > 0
      ? latestData.reduce((sum, r) => sum + (r.revenue_share || 0), 0) / latestData.length
      : 0;

  const cumulativeRevenue = revenue.reduce((sum, r) => sum + (r.revenue_contribution_usd || 0), 0);

  /* Total acquired users to date (d360's "Total Acquired Users"). Acquired users
     are new each quarter, so they add up over the title's life — unlike retained
     users, the same people kept quarter after quarter, which only make sense as
     a latest-quarter figure. */
  const cumulativeAcquired = revenue.reduce((sum, r) => sum + (r.subscribers_acquired || 0), 0);
  const cumulativeAcquiredUsd = revenue.reduce((sum, r) => sum + (r.acquired_usd || 0), 0);

  /* Real QoQ from the pre-aggregated time series: last two quarters' revenue.
     Demo heroes keep their curated QoQ; everything else uses real data or —.
     We never fabricate a value (spec §4). */
  const qoq = (() => {
    if (demoData) {
      return { pct: demoData.qoqTrendPct, direction: demoData.qoqDirection };
    }
    const sorted = [...timeSeries].sort((a, b) => a.quarter_end_date.localeCompare(b.quarter_end_date));
    if (sorted.length < 2) return undefined;
    const curr = sorted[sorted.length - 1].revenue_contribution_usd;
    const prev = sorted[sorted.length - 2].revenue_contribution_usd;
    if (!prev) return undefined;
    const pct = ((curr - prev) / prev) * 100;
    return { pct: Math.abs(pct), direction: directionOf(pct) };
  })();

  /* Per-quarter totals across every platform × region row — the trend lines. */
  const quarters = (() => {
    const by = new Map<string, { revenue: number; acquired: number; retained: number }>();
    for (const r of revenue) {
      const q = by.get(r.quarter_end_date) ?? { revenue: 0, acquired: 0, retained: 0 };
      q.revenue += r.revenue_contribution_usd || 0;
      q.acquired += r.subscribers_acquired || 0;
      q.retained += r.subscribers_retained || 0;
      by.set(r.quarter_end_date, q);
    }
    return [...by.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([quarter, v]) => ({ quarter, ...v }));
  })();
  const revenueSeries = quarters.map((q) => q.revenue);
  const runningTotal = (values: number[]) =>
    values.reduce<number[]>((acc, v) => [...acc, (acc[acc.length - 1] ?? 0) + v], []);
  const cumulativeSeries = runningTotal(revenueSeries);
  const cumulativeAcquiredSeries = runningTotal(quarters.map((q) => q.acquired));

  /** Quarter-on-quarter % for the last two points of a series; undefined if not computable. */
  const qoqOf = (values: number[]) => {
    if (values.length < 2) return undefined;
    const prev = values[values.length - 2];
    return prev ? ((values[values.length - 1] - prev) / prev) * 100 : undefined;
  };
  const revenueQoq = qoq ? (qoq.direction === 'down' ? -qoq.pct : qoq.pct) : undefined;

  /* Scope lines, d360-style: platforms, period, regions. */
  /* Platforms, biggest earner first (the banner shows the top three). */
  const platformsCovered = (() => {
    const by = new Map<string, number>();
    for (const r of revenue)
      by.set(r.availability_platform, (by.get(r.availability_platform) ?? 0) + (r.revenue_contribution_usd || 0));
    return [...by.entries()].sort((a, b) => b[1] - a[1]).map(([p]) => p);
  })();
  const regionsCovered = [...new Set(revenue.map((r) => r.region))];
  const platformScope =
    platformsCovered.length === 1 ? shortPlatform(platformsCovered[0]) : `${platformsCovered.length} platforms`;
  const regionScope = regionsCovered.length === 1 ? regionsCovered[0] : 'All regions';
  const firstQuarter = quarters[0]?.quarter;
  const span =
    firstQuarter && latestQuarter ? `${prettyQuarter(firstQuarter)} – ${prettyQuarter(latestQuarter)}` : '';
  const latest = latestQuarter ? prettyQuarter(latestQuarter) : '';
  const scope = (period: string) => [platformScope, period, regionScope].filter(Boolean).join(', ');

  /** Money without its sign — the widget draws a muted "$" itself. */
  const usd = (v: number) => (v > 0 ? formatUsdCompact(v).replace(/^\$/, '') : '—');
  const users = (v: number) => (v > 0 ? formatCount(v) : '—');
  const quarterLabels = quarters.map((q) => prettyQuarter(q.quarter));
  const change = (pct: number | undefined) =>
    pct === undefined ? undefined : <Delta value={pct} digits={Math.abs(pct) >= 100 ? 0 : 1} max={999} />;

  /* Compact sticky title bar (d360): appears once the hero has scrolled out, so
     any screenshot of a chart further down still says which title it is. The
     scroll container is the layout's <main>, so observe against that. */
  const heroEndRef = useRef<HTMLDivElement>(null);
  const [heroGone, setHeroGone] = useState(false);
  useEffect(() => {
    const sentinel = heroEndRef.current;
    if (!sentinel) return;
    const root = sentinel.closest('main');
    const io = new IntersectionObserver(
      ([entry]) => setHeroGone(!entry.isIntersecting && entry.boundingClientRect.top < (entry.rootBounds?.top ?? 0)),
      { root, threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
    // The sentinel only exists once the page (not the skeleton) has rendered.
  }, [titleLoading, revenueLoading, !!titleData]);

  const isLoading = titleLoading || revenueLoading;

  if (isLoading) {
    return <TitleProfileSkeleton />;
  }

  if (!title) {
    return (
      <div className={styles.error}>
        <p>Title not found</p>
        <button onClick={() => navigate(-1)} className={styles.backBtn}>
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      {/* Sticky compact header — zero-height anchor, bar fades in past the hero */}
      <div className={styles.stickyAnchor}>
        <div className={styles.stickyBar} data-visible={heroGone || undefined} aria-hidden={!heroGone}>
          <img
            src={`${POSTER_CDN}/${id}.jpg?width=96`}
            alt=""
            className={styles.stickyPoster}
            onError={(e) => {
              (e.target as HTMLImageElement).style.visibility = 'hidden';
            }}
          />
          <span className={styles.stickyTitle}>{title.display_name}</span>
          <span className={styles.stickyMeta}>
            {title.content_type === 'movie' ? 'Movie' : 'TV Show'}
            {title.release_year ? ` • ${title.release_year}` : ''}
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className={styles.hero}>
        <div
          className={styles.heroBg}
          style={{ backgroundImage: `url(${BG_CDN}/${id}.jpg?width=1920)` }}
        />
        <div className={styles.heroGradient} />
        <div className={styles.heroContent}>
          <button onClick={() => navigate(-1)} className={styles.backButton}>
            <ArrowLeft size={16} /> Back
          </button>

          <div className={styles.heroMain}>
            <img
              src={`${POSTER_CDN}/${id}.jpg?width=384`}
              alt={title.display_name}
              className={styles.heroPoster}
              onError={(e) => {
                (e.target as HTMLImageElement).style.visibility = 'hidden';
              }}
            />
            <div className={styles.heroInfo}>
              <div className={styles.heroTitleRow}>
                <h1 className={styles.heroTitle}>{title.display_name}</h1>
                {rankData?.ownRank ? (
                  <span className={styles.heroRank} title="Rank by all-time revenue across all platforms and regions">
                    <span className={styles.heroRankPound}>#</span>
                    {rankData.ownRank.toLocaleString()}
                    <span className={styles.heroRankLabel}>all-time revenue</span>
                  </span>
                ) : null}
              </div>

              {/* Line 1 — what it is: genres (gold), year, form */}
              <div className={styles.heroFacts}>
                {[title.main_genre, title.sub_genre].filter(Boolean).length > 0 && (
                  <span className={styles.heroGenres}>
                    {[title.main_genre, title.sub_genre].filter(Boolean).map(titleCase).join(', ')}
                  </span>
                )}
                {title.release_year ? <span>{title.release_year}</span> : null}
                <span>{title.content_type === 'movie' ? 'Movie' : 'TV Show'}</span>
                {title.content_sub_type && title.content_sub_type.toLowerCase() !== 'movie' && (
                  <span>{title.content_sub_type}</span>
                )}
              </div>

              {/* Line 2 — where it lives: platforms, origin, language, size */}
              <div className={styles.heroFacts}>
                {platformsCovered.length > 0 && (
                  <span className={styles.heroFact}>
                    {platformsCovered.slice(0, 3).map((p) => (
                      <span key={p} className={styles.heroPlatform}>
                        <PlatformIcon name={p} size={18} />
                        {shortPlatform(p)}
                      </span>
                    ))}
                    {platformsCovered.length > 3 && (
                      <span className={styles.heroMore}>+{platformsCovered.length - 3}</span>
                    )}
                  </span>
                )}
                {title.countries && (
                  <span className={styles.heroFact}>
                    {title.countries
                      .split(',')
                      .map((c) => c.trim())
                      .filter(Boolean)
                      .map((c) => (
                        <span key={c} className={styles.heroPlatform}>
                          <span className={styles.heroFlag} aria-hidden="true">
                            {flagOf(c)}
                          </span>
                          {nameOf(regionNames, c)}
                        </span>
                      ))}
                  </span>
                )}
                {title.original_language && (
                  <span className={styles.heroFact}>
                    <Languages size={15} className={styles.heroFactIcon} aria-hidden="true" />
                    {nameOf(languageNames, title.original_language)}
                  </span>
                )}
                {title.content_type !== 'movie' && title.total_seasons ? (
                  <span className={styles.heroFact}>
                    <Film size={15} className={styles.heroFactIcon} aria-hidden="true" />
                    {title.total_seasons} Season{title.total_seasons !== 1 ? 's' : ''}
                    {title.total_episodes ? ` (${title.total_episodes} Episodes)` : ''}
                  </span>
                ) : null}
              </div>

              {title.short_description && <p className={styles.heroDescription}>{title.short_description}</p>}

              {title.imdb_id && (
                <div className={styles.heroActions}>
                  <a
                    className={styles.heroAction}
                    href={`https://www.imdb.com/title/${title.imdb_id}/`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    IMDb <ExternalLink size={13} aria-hidden="true" />
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div ref={heroEndRef} aria-hidden="true" />

      {/* Stat widgets — d360 profile layout: each names its scope. Ranks come later. */}
      <div className={styles.kpiGrid}>
        <StatWidget
          title="Total Streaming Revenue"
          scope={scope(span)}
          unit="usd"
          value={usd(cumulativeRevenue)}
          tone="revenue"
          trend={cumulativeSeries}
          trendLabels={quarterLabels}
          formatTrend={formatUsdCompact}
          hint="Total revenue (USD) to date, across all platforms the title was available on, in all regions."
        />
        <StatWidget
          title="Latest Quarter Revenue"
          scope={scope(latest)}
          unit="usd"
          value={usd(totalRevenue)}
          tone="revenue"
          trend={revenueSeries}
          trendLabels={quarterLabels}
          formatTrend={formatUsdCompact}
          aside={change(revenueQoq)}
          hint={`Revenue in ${latest || 'the latest quarter'}, and its change on the quarter before. Average share of platform revenue: ${formatShare(avgShare)}.`}
        />
        <StatWidget
          title="Total Acquired Users"
          scope={scope(span)}
          unit="users"
          value={users(cumulativeAcquired)}
          tone="acquired"
          trend={cumulativeAcquiredSeries}
          trendLabels={quarterLabels}
          formatTrend={formatCount}
          hint={`Total acquired users to date, across all platforms the title was available on, in all regions — worth ${formatUsd(cumulativeAcquiredUsd)} in acquisition value.`}
        />
        <StatWidget
          title="Retained Users"
          scope={scope(latest)}
          unit="users"
          value={users(totalSubsRetained)}
          tone="retained"
          trend={quarters.map((q) => q.retained)}
          trendLabels={quarterLabels}
          formatTrend={formatCount}
          aside={change(qoqOf(quarters.map((q) => q.retained)))}
          hint={`Existing subscribers kept by this title in ${latest || 'the latest quarter'} — worth ${formatUsd(totalRetained)} in retention value.`}
        />
      </div>

      {/* Streaming-economics charts (d360 parity) */}
      {revenueError ? (
        <div className={styles.chartsError}>Revenue data failed to load. Try reloading the page.</div>
      ) : revenue.length > 0 ? (
        <div className={styles.charts}>
          <RevenueOverTimeChart id={id!} title={title.display_name} revenue={revenue} />
          <AcquisitionRetentionChart id={id!} title={title.display_name} revenue={revenue} />
        </div>
      ) : (
        <div className={styles.chartsError}>No revenue data available for this title.</div>
      )}

      {/* Season Breakdown — demo-only overlay for the 8 hero titles. */}
      {demoData?.seasons && demoData.seasons.length > 0 && (
        <div className={styles.seasonSection}>
          <div className={styles.sectionHeader}>
            <Calendar size={20} />
            <h2 className={styles.sectionTitle}>Season Breakdown</h2>
          </div>
          {demoData.narrativeInsight && <p className={styles.narrativeInsight}>{demoData.narrativeInsight}</p>}
          <div className={styles.seasonTableWrapper}>
            <table className={styles.seasonTable}>
              <thead>
                <tr>
                  <th>Season</th>
                  <th>Episodes</th>
                  <th>Release Date</th>
                  <th>Release Pattern</th>
                  <th>Revenue</th>
                  <th>Per Episode</th>
                  <th>Peak Rank</th>
                </tr>
              </thead>
              <tbody>
                {demoData.seasons.map((season) => (
                  <tr key={season.season}>
                    <td className={styles.seasonNum}>S{season.season}</td>
                    <td>{season.episodes}</td>
                    <td>
                      {new Date(season.releaseDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </td>
                    <td>
                      <span
                        className={`${styles.releaseBadge} ${season.releasePattern === 'weekly' ? styles.weekly : styles.binge}`}
                      >
                        {season.releasePattern === 'weekly' ? 'Weekly' : 'All at once'}
                      </span>
                    </td>
                    <td className={styles.revenueCell}>{formatUsd(season.revenueUsd)}</td>
                    <td className={styles.perEpisodeCell}>{formatUsd(season.perEpisodeUsd)}</td>
                    <td className={styles.rankCell}>#{season.peakRank}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
